/**
 * src/components/agent/LanguagePickerSheet.jsx — Language hint picker (Phase 82-05).
 *
 * Bottom sheet that lists Auto / English / Español / Mix. Tap sets the
 * language hint for subsequent voice.chat posts + STT provider.
 */

import { LANGUAGES } from '../../lib/voice/voiceOptions';
import { useT } from '../../lib/i18n/I18nContext';

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.35)',
  zIndex: 10010,
};

const sheetStyle = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  background: 'var(--card)',
  color: 'var(--t1)',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  boxShadow: 'var(--she)',
  zIndex: 10011,
  padding: '12px 0 calc(24px + env(safe-area-inset-bottom))',
  maxHeight: '60vh',
  overflowY: 'auto',
};

const titleStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--tm)',
  padding: '4px 20px 12px',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const rowStyle = (active) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 20px',
  minHeight: 56,
  cursor: 'pointer',
  background: active ? 'var(--accent-bg)' : 'transparent',
  color: 'var(--t1)',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  fontSize: 15,
});

const codeStyle = {
  minWidth: 44,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--accent)',
  textTransform: 'uppercase',
};

export default function LanguagePickerSheet({ open, currentLanguageId, onSelect, onClose }) {
  const { t } = useT();
  if (!open) return null;
  return (
    <>
      <div
        style={backdropStyle}
        onClick={onClose}
        role="button"
        tabIndex={0}
        aria-label={t('agentChat.closeLanguagePicker')}
        onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') onClose?.(); }}
        data-testid="language-picker-backdrop"
      />
      <div style={sheetStyle} role="dialog" aria-label="Pick language" data-testid="language-picker-sheet">
        <div style={titleStyle}>Language</div>
        {LANGUAGES.map((l) => {
          const active = l.id === currentLanguageId;
          return (
            <button
              key={l.id}
              type="button"
              style={rowStyle(active)}
              onClick={() => { onSelect?.(l.id); onClose?.(); }}
              data-testid={`language-picker-row-${l.id}`}
              aria-pressed={active}
            >
              <span style={codeStyle} aria-hidden="true">{l.id}</span>
              <span>{l.displayName}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
